/* eslint-disable no-unused-vars */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable global-require */
import axios from 'axios';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useContext } from 'react';
import { Col, Row, Container, Modal, Button, Form } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { SpinnerDotted } from 'spinners-react';
import { toast } from 'react-toastify';
import { getCookies } from '../../hooks/useCookies';
import { TableContext } from '../../contexts/tableContext';
import styles from './viewUser.module.scss';
import { DateFormatter } from '../../utilityFunctions/utilsFunctions';
import envValues from '../../enviornment';

/**
 * Rounds avatar image with initials fallback when URL is missing or fails to load.
 */
function UserAvatarWithFallback({ imageUrl, name, username, size, className, style }) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [imageUrl]);

  const display = (name || username || '?').trim() || '?';
  const initial = display.charAt(0).toUpperCase();
  const dimension = `${size}px`;
  const baseStyle = {
    width: dimension,
    height: dimension,
    borderRadius: '50%',
    flexShrink: 0,
    objectFit: 'cover',
    ...style,
  };

  if (!imageUrl || failed) {
    return (
      <div
        className={`${styles.avatarFallback} ${className || ''}`}
        style={{
          ...baseStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#e5e7eb',
          color: '#4b5563',
          fontWeight: 600,
          fontSize: Math.max(12, Math.round(size * 0.38)),
        }}
        aria-hidden
      >
        {initial}
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={display}
      className={className}
      style={baseStyle}
      onError={() => setFailed(true)}
    />
  );
}

const stylePropType = PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]));

UserAvatarWithFallback.propTypes = {
  imageUrl: PropTypes.string,
  name: PropTypes.string,
  username: PropTypes.string,
  size: PropTypes.number.isRequired,
  className: PropTypes.string,
  style: stylePropType,
};

UserAvatarWithFallback.defaultProps = {
  imageUrl: '',
  name: '',
  username: '',
  className: '',
  style: undefined,
};

/** API uses love_leaves; admin UI shows "coin" for wallet transactions. */
function formatTransactionCurrencyLabel(currencyType) {
  if (!currencyType || currencyType === 'love_leaves') return 'coin';
  return currencyType;
}

/** e.g. item_purchase → Item purchase (snake_case from API ref.type). */
function formatActionDescription(description) {
  if (!description) return '';
  const spaced = String(description).replace(/_/g, ' ');
  return spaced.replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function ViewUsers() {
  const { dashboardStyle } = useContext(TableContext);
  const { setDashboardHeader } = useContext(TableContext);
  const { bodyStyle } = useContext(TableContext);
  const { formthemeStyle } = useContext(TableContext);
  const { viewformStyle } = useContext(TableContext);
  const params = useParams();
  const { userId } = params;
  const token = `Bearer ${getCookies('Token')}`;
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState([]);
  const [roles, setRole] = useState([]);
  const [friends, setFriends] = useState([]);
  const [friendsLoading, setFriendsLoading] = useState(false);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [showFriendModal, setShowFriendModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [suspendDuration, setSuspendDuration] = useState('');
  const [showAdjustCurrencyModal, setShowAdjustCurrencyModal] = useState(false);
  // const [showCancelSubscriptionModal, setShowCancelSubscriptionModal] = useState(false);
  const [showViewActionsModal, setShowViewActionsModal] = useState(false);
  const [currencyAmount, setCurrencyAmount] = useState('');
  const [currencyAction, setCurrencyAction] = useState('add'); // 'add' or 'subtract'
  const [currencyReason, setCurrencyReason] = useState('');
  const [userActions, setUserActions] = useState([]);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showUnsuspendModal, setShowUnsuspendModal] = useState(false);
  const [unsuspendReason, setUnsuspendReason] = useState('');
  const dateFormat = getCookies('dateFormat');
  let statusCode;

  const renderPremiumStatus = () => {
    if (details.isTrialPeriod) {
      return (
        <>
          <span className={styles.trialBadge}>Trial</span>
          {details.trialEndsAt && (
            <span className={styles.trialEndsAt}>
              {' '}
              · ends {DateFormatter(details.trialEndsAt, dateFormat || 'toLocaleDateString')}
            </span>
          )}
        </>
      );
    }

    if (details.premiumStatus) {
      return <span className={styles.premiumBadge}>Premium</span>;
    }

    return <span className={styles.freeBadge}>Free</span>;
  };

  const getUserDetails = async () => {
    setLoading(true);
    axios
      .get(`${envValues.REACT_APP_API_ENDPOINT}/user/${userId}`, {
        method: 'GET',
        headers: { Authorization: token },
      })
      .then((resp) => {
        statusCode = resp.status;
        return resp.data;
      })
      .then((res) => {
        if (statusCode === 200 && res && res.success && res.data && res.data.user) {
          setDetails(res.data.user);
          setRole(res.data.user.role || {});
          setLoading(false);
        } else {
          setLoading(false);
        }
      })
      .catch((err) => {
        setLoading(false);
        return err;
      });
  };

  const navigate = useNavigate();

  // Helper function to format image URL with protocol if missing
  const formatImageUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    if (url.startsWith('//')) return `https:${url}`;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `https://${url}`;
  };

  // Get the user's avatar/image URL
  const getUserImageUrl = () => {
    const imageUrl =
      details.avatarUrl ||
      (details.avatar && typeof details.avatar === 'object'
        ? details.avatar.image
        : details.avatar) ||
      details.image ||
      '';
    return formatImageUrl(imageUrl);
  };

  // Get friend's avatar/image URL (avatar may be object, string, or missing)
  const getFriendImageUrl = (friend) => {
    if (!friend) return '';
    const raw =
      (friend.avatar && typeof friend.avatar === 'object' ? friend.avatar.image : friend.avatar) ||
      friend.image ||
      '';
    return formatImageUrl(raw);
  };

  // Get tree status badge background color
  const getTreeStatusBgColor = (healthStatus) => {
    if (healthStatus === 'healthy') return '#d1fae5';
    if (healthStatus === 'needs_water') return '#fef3c7';
    return '#fee2e2';
  };

  // Get tree status badge text color
  const getTreeStatusTextColor = (healthStatus) => {
    if (healthStatus === 'healthy') return '#065f46';
    if (healthStatus === 'needs_water') return '#92400e';
    return '#991b1b';
  };

  // Get tree status label text
  const getTreeStatusLabel = (healthStatus) => {
    if (healthStatus === 'healthy') return 'Healthy';
    if (healthStatus === 'needs_water') return 'Needs Water';
    return 'Withered';
  };

  // Handle friend row click
  const handleFriendClick = (friend) => {
    setSelectedFriend(friend);
    setShowFriendModal(true);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowFriendModal(false);
    setSelectedFriend(null);
  };

  // Handle Delete User
  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setActionLoading(true);
    try {
      const response = await axios.delete(`${envValues.REACT_APP_API_ENDPOINT}/user/${userId}`, {
        headers: { Authorization: token },
      });
      if (response.status === 200) {
        toast.success('User deleted successfully');
        setShowDeleteModal(false);
        navigate(-1);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete user');
      setActionLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  // Handle Suspend User
  const handleSuspendClick = () => {
    setSuspendReason('');
    setSuspendDuration('');
    setShowSuspendModal(true);
  };

  const handleSuspendConfirm = async () => {
    if (!suspendReason || suspendReason.trim().length < 3) {
      toast.error('Please provide a suspension reason (min 3 characters)');
      return;
    }
    setActionLoading(true);
    try {
      const durationMinutes = suspendDuration ? parseInt(suspendDuration, 10) : null;
      const response = await axios.patch(
        `${envValues.REACT_APP_API_ENDPOINT}/user/${userId}/suspend`,
        { reason: suspendReason.trim(), durationMinutes },
        {
          headers: { Authorization: token },
        }
      );
      if (response.status === 200) {
        toast.success('User suspended successfully');
        setShowSuspendModal(false);
        getUserDetails(); // Refresh user details
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to suspend user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspendCancel = () => {
    setShowSuspendModal(false);
  };

  // Handle Unsuspend User
  const handleUnsuspendClick = () => {
    setUnsuspendReason('');
    setShowUnsuspendModal(true);
  };

  const handleUnsuspendConfirm = async () => {
    if (!unsuspendReason || unsuspendReason.trim().length < 3) {
      toast.error('Please provide an unsuspend reason (min 3 characters)');
      return;
    }
    setActionLoading(true);
    try {
      const response = await axios.patch(
        `${envValues.REACT_APP_API_ENDPOINT}/user/${userId}/unsuspend`,
        { reason: unsuspendReason.trim() },
        { headers: { Authorization: token } }
      );
      if (response.status === 200) {
        toast.success('User unsuspended successfully');
        setShowUnsuspendModal(false);
        getUserDetails();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to unsuspend user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnsuspendCancel = () => {
    setShowUnsuspendModal(false);
  };

  // Handle Adjust Currency
  const handleAdjustCurrencyClick = () => {
    setCurrencyAmount('');
    setCurrencyAction('add');
    setCurrencyReason('');
    setShowAdjustCurrencyModal(true);
  };

  const handleAdjustCurrencyConfirm = async () => {
    if (
      !currencyAmount ||
      Number.isNaN(parseFloat(currencyAmount)) ||
      parseFloat(currencyAmount) <= 0
    ) {
      toast.error('Please enter a valid currency amount');
      return;
    }

    if (!currencyReason || currencyReason.trim().length < 3) {
      toast.error('Please provide a reason for currency adjustment (min 3 characters)');
      return;
    }

    // Check if subtracting would result in negative balance
    const currentBalance = details.currencyBalance || 0;
    const amountToSubtract = parseFloat(currencyAmount);
    if (currencyAction === 'subtract' && currentBalance - amountToSubtract < 0) {
      toast.error(
        `Cannot subtract ${amountToSubtract} coins. Current balance is ${currentBalance} coins. Balance cannot be negative.`
      );
      return;
    }

    setActionLoading(true);
    try {
      const amount =
        currencyAction === 'add' ? parseFloat(currencyAmount) : -parseFloat(currencyAmount);
      const response = await axios.patch(
        `${envValues.REACT_APP_API_ENDPOINT}/user/${userId}/currency`,
        { amount, reason: currencyReason.trim() },
        {
          headers: { Authorization: token },
        }
      );
      if (response.status === 200) {
        toast.success(`Currency ${currencyAction === 'add' ? 'added' : 'subtracted'} successfully`);
        setShowAdjustCurrencyModal(false);
        setCurrencyAmount('');
        setCurrencyReason('');
        getUserDetails(); // Refresh user details
        setActionLoading(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to adjust currency');
      setActionLoading(false);
    }
  };

  const handleAdjustCurrencyCancel = () => {
    setShowAdjustCurrencyModal(false);
    setCurrencyAmount('');
    setCurrencyReason('');
  };

  // Handle Cancel Subscription — hidden (not in proposal)
  // const handleCancelSubscriptionClick = () => {
  //   setShowCancelSubscriptionModal(true);
  // };

  // const handleCancelSubscriptionConfirm = async () => {
  //   setActionLoading(true);
  //   try {
  //     const response = await axios.patch(
  //       `${envValues.REACT_APP_API_ENDPOINT}/user/${userId}/subscription/cancel`,
  //       {},
  //       {
  //         headers: { Authorization: token },
  //       }
  //     );
  //     if (response.status === 200) {
  //       toast.success('Subscription cancelled successfully');
  //       setShowCancelSubscriptionModal(false);
  //       getUserDetails(); // Refresh user details
  //       setActionLoading(false);
  //     }
  //   } catch (error) {
  //     toast.error(error.response?.data?.message || 'Failed to cancel subscription');
  //     setActionLoading(false);
  //   }
  // };

  // const handleCancelSubscriptionCancel = () => {
  //   setShowCancelSubscriptionModal(false);
  // };

  // Handle View User Actions
  const handleViewActionsClick = async () => {
    setShowViewActionsModal(true);
    setActionsLoading(true);
    try {
      const response = await axios.get(
        `${envValues.REACT_APP_API_ENDPOINT}/user/${userId}/actions`,
        {
          headers: { Authorization: token },
        }
      );
      if (response.status === 200 && response.data && response.data.success) {
        setUserActions(response.data.data?.actions || []);
      }
    } catch (error) {
      console.error('Error fetching user actions:', error);
      setUserActions([]);
    } finally {
      setActionsLoading(false);
    }
  };

  const handleViewActionsClose = () => {
    setShowViewActionsModal(false);
    setUserActions([]);
  };

  // Helper function to format duration
  const formatDuration = (durationMinutes) => {
    if (!durationMinutes) return 'Indefinite';
    if (durationMinutes === 1440) return '24 hours';
    if (durationMinutes === 10080) return '7 days';
    if (durationMinutes === 43200) return '30 days';
    return `${durationMinutes} minutes`;
  };

  // Get user's friends with tree statistics
  const getUserFriends = async () => {
    setFriendsLoading(true);
    try {
      const response = await axios.get(
        `${envValues.REACT_APP_API_ENDPOINT}/user/${userId}/friends`,
        {
          method: 'GET',
          headers: { Authorization: token },
        }
      );
      if (response.status === 200 && response.data && response.data.success && response.data.data) {
        setFriends(response.data.data.friends || []);
      }
    } catch (error) {
      console.error('Error fetching friends:', error);
      setFriends([]);
    } finally {
      setFriendsLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getUserDetails();
    getUserFriends();
    setDashboardHeader('User Details');
  }, []);

  return (
    <div className={styles[bodyStyle]}>
      <div className={styles[dashboardStyle]} id={styles.dashboardcont2}>
        <div className="form_head">
          <div
            value="Back"
            onClick={() => {
              navigate(-1);
            }}
            className={`${styles.arrowback} backBtn`}
          >
            <svg
              className="mr-1"
              width="17"
              height="14"
              viewBox="0 0 17 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.5 6.00003H3.64L7.27 1.64003C7.43974 1.43581 7.5214 1.17253 7.49702 0.908108C7.47264 0.643682 7.34422 0.39977 7.14 0.230032C6.93578 0.060293 6.6725 -0.0213689 6.40808 0.0030108C6.14365 0.0273905 5.89974 0.155815 5.73 0.360032L0.73 6.36003C0.696361 6.40776 0.666279 6.45789 0.64 6.51003C0.64 6.56003 0.64 6.59003 0.57 6.64003C0.524674 6.75469 0.500941 6.87674 0.5 7.00003C0.500941 7.12332 0.524674 7.24537 0.57 7.36003C0.57 7.41003 0.57 7.44003 0.64 7.49003C0.666279 7.54217 0.696361 7.59231 0.73 7.64003L5.73 13.64C5.82402 13.7529 5.94176 13.8437 6.07485 13.9059C6.20793 13.9681 6.35309 14.0003 6.5 14C6.73365 14.0005 6.96009 13.9191 7.14 13.77C7.24126 13.6861 7.32496 13.583 7.38631 13.4666C7.44766 13.3503 7.48546 13.223 7.49754 13.092C7.50961 12.961 7.49573 12.829 7.45669 12.7034C7.41764 12.5777 7.3542 12.4611 7.27 12.36L3.64 8.00003H15.5C15.7652 8.00003 16.0196 7.89467 16.2071 7.70714C16.3946 7.5196 16.5 7.26525 16.5 7.00003C16.5 6.73482 16.3946 6.48046 16.2071 6.29292C16.0196 6.10539 15.7652 6.00003 15.5 6.00003Z"
                fill=""
              />
            </svg>
            Back
          </div>
        </div>
        <div
          className={`${styles[formthemeStyle]} ${styles.profile_view_container} profile_view_container`}
          id={styles.addform}
        >
          {loading ? (
            <SpinnerDotted
              style={{
                color: '#39979d',
                left: '50%',
                position: 'relative',
                textAlign: 'center',
                top: '50%',
              }}
            />
          ) : (
            <Row>
              <Col lg={12}>
                <div
                  className={styles.profilepicdiv}
                  style={{ marginBottom: '30px', textAlign: 'center' }}
                >
                  <UserAvatarWithFallback
                    imageUrl={getUserImageUrl()}
                    name={details.name}
                    username={details.username}
                    size={150}
                    className={styles.profilepic}
                  />
                </div>
              </Col>
              <Col lg={6} xs={12}>
                <span className={styles.title}>User ID</span>
                <div className={styles[viewformStyle]} id={styles.empdiv}>
                  <p className={styles.empname} style={{ wordBreak: 'break-all' }}>
                    {details.id || '-'}
                  </p>
                </div>
                <span className={styles.title}>Name</span>
                <div className={styles[viewformStyle]} id={styles.empdiv}>
                  <p className={styles.empname} style={{ wordBreak: 'break-word' }}>
                    {details.name || '-'}
                  </p>
                </div>
                <span className={styles.title}>Email</span>
                <div className={styles[viewformStyle]} id={styles.empdiv}>
                  <p className={styles.empname} style={{ wordBreak: 'break-word' }}>
                    {details.email || '-'}
                  </p>
                </div>
              </Col>
              <Col lg={6} xs={12}>
                <span className={styles.title}>Created On</span>
                <div className={styles[viewformStyle]} id={styles.empdiv}>
                  <p className={styles.empname} style={{ wordBreak: 'break-word' }}>
                    {details.createdAt
                      ? DateFormatter(details.createdAt, dateFormat || 'toLocaleDateString')
                      : '-'}
                  </p>
                </div>
                <span className={styles.title}>Last Active</span>
                <div className={styles[viewformStyle]} id={styles.empdiv}>
                  <p className={styles.empname} style={{ wordBreak: 'break-word' }}>
                    {details.lastActiveAt
                      ? DateFormatter(details.lastActiveAt, dateFormat || 'toLocaleDateString')
                      : 'Never'}
                  </p>
                </div>
                <span className={styles.title}>Account Status</span>
                <div className={styles[viewformStyle]} id={styles.empdiv}>
                  <p className={styles.empname} style={{ wordBreak: 'break-word' }}>
                    {details.active ? 'Active' : 'Inactive'}
                  </p>
                </div>
              </Col>
              <Row style={{ margin: 0 }}>
                <Col xs={12} sm={6} style={{ paddingLeft: 0, paddingRight: '10px' }}>
                  <span className={styles.title}>Premium Status</span>
                  <div className={styles[viewformStyle]} id={styles.empdiv}>
                    <p className={styles.empname} style={{ wordBreak: 'break-word' }}>
                      {renderPremiumStatus()}
                    </p>
                  </div>
                </Col>
                <Col xs={12} sm={6} style={{ paddingLeft: '0px', paddingRight: 0 }}>
                  <span className={styles.title}>Currency Balance</span>
                  <div className={styles[viewformStyle]} id={styles.empdiv}>
                    <p className={styles.empname} style={{ wordBreak: 'break-word' }}>
                      {details.currencyBalance || 0} coins
                    </p>
                  </div>
                </Col>
              </Row>
            </Row>
          )}
        </div>

        {/* Account Control Actions Section */}
        <div
          className={`${styles[formthemeStyle]} ${styles.profile_view_container} profile_view_container`}
          id={styles.addform}
          style={{ marginTop: '30px' }}
        >
          <h3 className={styles.sectionTitle} style={{ marginBottom: '20px' }}>
            Account Control Actions
          </h3>
          <div className={styles.actionButtonsContainer}>
            <Button
              variant="outline-danger"
              className={styles.actionButton}
              onClick={handleDeleteClick}
              disabled={actionLoading}
            >
              <i className="fa fa-trash" style={{ marginRight: '8px' }} />
              Delete User
            </Button>
            {details.active ? (
              <Button
                variant="outline-warning"
                className={styles.actionButton}
                onClick={handleSuspendClick}
                disabled={actionLoading}
              >
                <i className="fa fa-ban" style={{ marginRight: '8px' }} />
                Suspend User
              </Button>
            ) : (
              <Button
                variant="outline-success"
                className={styles.actionButton}
                onClick={handleUnsuspendClick}
                disabled={actionLoading}
              >
                <i className="fa fa-check-circle" style={{ marginRight: '8px' }} />
                Unsuspend User
              </Button>
            )}
            <Button
              variant="outline-info"
              className={styles.actionButton}
              onClick={handleViewActionsClick}
              disabled={actionLoading}
            >
              <i className="fa fa-history" style={{ marginRight: '8px' }} />
              View User Actions
            </Button>
            <Button
              variant="outline-primary"
              className={styles.actionButton}
              onClick={handleAdjustCurrencyClick}
              disabled={actionLoading}
            >
              <i className="fa fa-coins" style={{ marginRight: '8px' }} />
              Adjust Currency
            </Button>
            {/* Cancel Subscription — hidden (not in proposal)
            <Button
              variant="outline-secondary"
              className={styles.actionButton}
              onClick={handleCancelSubscriptionClick}
              disabled={actionLoading || !details.premiumStatus}
            >
              <i className="fa fa-times-circle" style={{ marginRight: '8px' }} />
              Cancel Subscription
            </Button>
            */}
          </div>
        </div>

        {/* Friends List Section */}
        <div
          className={`${styles[formthemeStyle]} ${styles.profile_view_container} profile_view_container`}
          id={styles.addform}
          style={{ marginTop: '30px' }}
        >
          <h3 className={styles.sectionTitle} style={{ marginBottom: '20px' }}>
            Friend List
          </h3>
          {friendsLoading && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <SpinnerDotted style={{ color: '#39979d' }} />
            </div>
          )}
          {!friendsLoading && friends.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              <p>No friends found</p>
            </div>
          )}
          {!friendsLoading && friends.length > 0 && (
            <div className={styles.friendsList}>
              {friends.map((friend) => (
                <div
                  key={friend.userId}
                  className={styles.friendListItem}
                  onClick={() => handleFriendClick(friend)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleFriendClick(friend);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                    <UserAvatarWithFallback
                      imageUrl={getFriendImageUrl(friend)}
                      name={friend.name}
                      username={friend.username}
                      size={40}
                      style={{ marginRight: '15px' }}
                    />
                    <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                      <h6
                        style={{
                          margin: 0,
                          fontSize: '15px',
                          fontWeight: '600',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={friend.name || friend.username || 'Unknown'}
                      >
                        {friend.name || friend.username || 'Unknown'}
                      </h6>
                      {friend.username && friend.name && (
                        <p
                          style={{
                            margin: 0,
                            fontSize: '13px',
                            color: '#6b7280',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title={`@${friend.username}`}
                        >
                          @{friend.username}
                        </p>
                      )}
                    </div>
                    {friend.tree && (
                      <span
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor: getTreeStatusBgColor(friend.tree.healthStatus),
                          color: getTreeStatusTextColor(friend.tree.healthStatus),
                          flexShrink: 0,
                          marginLeft: '10px',
                        }}
                      >
                        {getTreeStatusLabel(friend.tree.healthStatus)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Delete User Modal */}
        <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
          <Modal.Header closeButton>
            <Modal.Title>Delete User</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Are you sure you want to delete this user? This action cannot be undone.</p>
            {details.name && (
              <p style={{ fontWeight: 'bold', color: '#dc3545' }}>
                User: {details.name} (ID: {details.id})
              </p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleDeleteCancel} disabled={actionLoading}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteConfirm} disabled={actionLoading}>
              {actionLoading ? 'Deleting...' : 'Delete User'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Unsuspend User Modal */}
        <Modal show={showUnsuspendModal} onHide={handleUnsuspendCancel} centered>
          <Modal.Header closeButton>
            <Modal.Title>Unsuspend User</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              This will restore the user&apos;s access. Provide a reason for unsuspension for the
              audit log.
            </p>
            {details.name && (
              <p style={{ fontWeight: 'bold', color: '#16a34a' }}>
                User: {details.name} (ID: {details.id})
              </p>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Reason (required)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter unsuspension reason (min 3 characters)"
                value={unsuspendReason}
                onChange={(e) => setUnsuspendReason(e.target.value)}
                disabled={actionLoading}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleUnsuspendCancel} disabled={actionLoading}>
              Cancel
            </Button>
            <Button variant="success" onClick={handleUnsuspendConfirm} disabled={actionLoading}>
              {actionLoading ? 'Unsuspending...' : 'Unsuspend User'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Suspend User Modal */}
        <Modal show={showSuspendModal} onHide={handleSuspendCancel} centered>
          <Modal.Header closeButton>
            <Modal.Title>Suspend User</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              Are you sure you want to suspend this user? They will not be able to access their
              account.
            </p>
            {details.name && (
              <p style={{ fontWeight: 'bold', color: '#ffc107' }}>
                User: {details.name} (ID: {details.id})
              </p>
            )}
            <div style={{ marginTop: '12px' }}>
              <Form.Group className="mb-3">
                <Form.Label>Reason (required)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter suspension reason (min 3 characters)"
                  value={suspendReason}
                  onChange={(e) => setSuspendReason(e.target.value)}
                  disabled={actionLoading}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label>Duration</Form.Label>
                <Form.Select
                  value={suspendDuration}
                  onChange={(e) => setSuspendDuration(e.target.value)}
                  disabled={actionLoading}
                >
                  <option value="">Indefinite</option>
                  <option value="1440">24 hours</option>
                  <option value="10080">7 days</option>
                  <option value="43200">30 days</option>
                </Form.Select>
              </Form.Group>
              <small style={{ color: '#6b7280' }}>
                A reason is mandatory. Duration is optional; leave blank for indefinite suspension.
              </small>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleSuspendCancel} disabled={actionLoading}>
              Cancel
            </Button>
            <Button variant="warning" onClick={handleSuspendConfirm} disabled={actionLoading}>
              {actionLoading ? 'Suspending...' : 'Suspend User'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Adjust Currency Modal */}
        <Modal show={showAdjustCurrencyModal} onHide={handleAdjustCurrencyCancel} centered>
          <Modal.Header closeButton>
            <Modal.Title>Adjust Currency</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p style={{ marginBottom: '15px' }}>
              Current Balance: <strong>{details.currencyBalance || 0} coins</strong>
            </p>
            <Form.Group className="mb-3">
              <Form.Label>Action</Form.Label>
              <Form.Select
                value={currencyAction}
                onChange={(e) => setCurrencyAction(e.target.value)}
                disabled={actionLoading}
              >
                <option value="add">Add Currency</option>
                <option value="subtract">Subtract Currency</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Amount</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                value={currencyAmount}
                onChange={(e) => setCurrencyAmount(e.target.value)}
                placeholder="Enter amount"
                disabled={actionLoading}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Reason (required)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter reason for currency adjustment (min 3 characters)"
                value={currencyReason}
                onChange={(e) => setCurrencyReason(e.target.value)}
                disabled={actionLoading}
              />
            </Form.Group>
            {currencyAmount &&
              !Number.isNaN(parseFloat(currencyAmount)) &&
              parseFloat(currencyAmount) > 0 && (
                <div
                  style={{
                    marginTop: '10px',
                    padding: '10px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '5px',
                  }}
                >
                  <p style={{ margin: 0 }}>
                    New Balance:{' '}
                    <strong>
                      {currencyAction === 'add'
                        ? (details.currencyBalance || 0) + parseFloat(currencyAmount)
                        : Math.max(
                            0,
                            (details.currencyBalance || 0) - parseFloat(currencyAmount)
                          )}{' '}
                      coins
                    </strong>
                  </p>
                </div>
              )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={handleAdjustCurrencyCancel}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAdjustCurrencyConfirm}
              disabled={actionLoading}
            >
              {actionLoading ? 'Processing...' : 'Confirm'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Cancel Subscription Modal — hidden (not in proposal)
        <Modal show={showCancelSubscriptionModal} onHide={handleCancelSubscriptionCancel} centered>
          <Modal.Header closeButton>
            <Modal.Title>Cancel Subscription</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              Are you sure you want to cancel this user&apos;s subscription? They will lose premium
              access.
            </p>
            {details.name && (
              <p style={{ fontWeight: 'bold', color: '#6c757d' }}>
                User: {details.name} (ID: {details.id})
              </p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={handleCancelSubscriptionCancel}
              disabled={actionLoading}
            >
              Cancel
            </Button>
            <Button
              variant="secondary"
              onClick={handleCancelSubscriptionConfirm}
              disabled={actionLoading}
            >
              {actionLoading ? 'Cancelling...' : 'Cancel Subscription'}
            </Button>
          </Modal.Footer>
        </Modal>
        */}

        {/* View User Actions Modal */}
        <Modal show={showViewActionsModal} onHide={handleViewActionsClose} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>User Actions History</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {actionsLoading && (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <SpinnerDotted style={{ color: '#39979d' }} />
              </div>
            )}
            {!actionsLoading && userActions.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                <p>No actions found for this user</p>
              </div>
            )}
            {!actionsLoading && userActions.length > 0 && (
              <div className={styles.actionsList}>
                {userActions.map((action) => {
                  const actionKey =
                    action.id ||
                    action.timestamp ||
                    `${action.type || action.action || 'action'}-${action.timestamp || Date.now()}`;
                  return (
                    <div key={actionKey} className={styles.actionItem}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <h6 style={{ margin: 0, fontSize: '15px', fontWeight: '600' }}>
                            {action.action || action.type || 'Unknown Action'}
                          </h6>
                          {action.description && (
                            <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                              {formatActionDescription(action.description)}
                            </p>
                          )}
                          {action.details && (
                            <div style={{ marginTop: 6, fontSize: '13px', color: '#4b5563' }}>
                              {action.action === 'suspend' && (
                                <div>
                                  <div>Reason: {action.details.reason || 'Not provided'}</div>
                                  <div>
                                    Duration: {formatDuration(action.details.durationMinutes)}
                                  </div>
                                  {action.details.resumeAt && (
                                    <div>
                                      Resumes:{' '}
                                      {DateFormatter(
                                        action.details.resumeAt,
                                        dateFormat || 'toLocaleDateString'
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                              {action.action === 'unsuspend' && (
                                <div>
                                  <div>Reason: {action.details.reason || 'Not provided'}</div>
                                </div>
                              )}
                              {action.action === 'adjust_currency' && action.details.meta && (
                                <div>
                                  <div>
                                    Amount: {action.details.meta.amount > 0 ? '+' : ''}
                                    {action.details.meta.amount} coins
                                  </div>
                                  {action.details.meta.currentBalance !== undefined && (
                                    <div>
                                      Previous Balance: {action.details.meta.currentBalance} coins
                                    </div>
                                  )}
                                  {action.details.meta.newBalance !== undefined && (
                                    <div>New Balance: {action.details.meta.newBalance} coins</div>
                                  )}
                                </div>
                              )}
                              {action.action === 'cancel_subscription' && action.details.meta && (
                                <div>
                                  {action.details.meta.subscriptionId && (
                                    <div>Subscription ID: {action.details.meta.subscriptionId}</div>
                                  )}
                                  {action.details.meta.platform && (
                                    <div>Platform: {action.details.meta.platform}</div>
                                  )}
                                  {action.details.meta.status && (
                                    <div>Status: {action.details.meta.status}</div>
                                  )}
                                  {action.details.meta.since && (
                                    <div>
                                      Started:{' '}
                                      {DateFormatter(
                                        action.details.meta.since,
                                        dateFormat || 'toLocaleDateString'
                                      )}
                                    </div>
                                  )}
                                  {action.details.meta.nextRenewalAt && (
                                    <div>
                                      Next Renewal:{' '}
                                      {DateFormatter(
                                        action.details.meta.nextRenewalAt,
                                        dateFormat || 'toLocaleDateString'
                                      )}
                                    </div>
                                  )}
                                  {action.details.meta.cancelledAt && (
                                    <div>
                                      Cancelled:{' '}
                                      {DateFormatter(
                                        action.details.meta.cancelledAt,
                                        dateFormat || 'toLocaleDateString'
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                              {action.action === 'purchase' && (
                                <div>
                                  {action.details.itemName && (
                                    <div>
                                      <span style={{ fontWeight: 600 }}>Item:</span>{' '}
                                      <span style={{ fontWeight: 400 }}>
                                        {action.details.itemName}
                                      </span>
                                    </div>
                                  )}
                                  {action.details.itemId && (
                                    <div>
                                      <span style={{ fontWeight: 600 }}>Item ID:</span>{' '}
                                      <span style={{ fontWeight: 400 }}>
                                        {action.details.itemId}
                                      </span>
                                    </div>
                                  )}
                                  {action.details.amount !== undefined && (
                                    <div>
                                      <span style={{ fontWeight: 600 }}>Cost:</span>{' '}
                                      <span style={{ fontWeight: 400 }}>
                                        {action.details.amount}{' '}
                                        {formatTransactionCurrencyLabel(
                                          action.details.currencyType
                                        )}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                              {action.action !== 'suspend' &&
                                action.action !== 'unsuspend' &&
                                action.action !== 'adjust_currency' &&
                                action.action !== 'cancel_subscription' &&
                                action.details.meta && (
                                  <div>Details: {JSON.stringify(action.details.meta)}</div>
                                )}
                              {action.details.amount !== undefined &&
                                action.action !== 'adjust_currency' &&
                                action.action !== 'purchase' && (
                                  <div>
                                    <span style={{ fontWeight: 600 }}>Amount:</span>{' '}
                                    <span style={{ fontWeight: 400 }}>
                                      {action.details.amount}{' '}
                                      {formatTransactionCurrencyLabel(action.details.currencyType)}
                                    </span>
                                  </div>
                                )}
                            </div>
                          )}
                        </div>
                        <div style={{ marginLeft: '15px', textAlign: 'right' }}>
                          {action.timestamp && (
                            <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af' }}>
                              {DateFormatter(action.timestamp, dateFormat || 'toLocaleDateString')}
                            </p>
                          )}
                          {action.ipAddress && (
                            <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#9ca3af' }}>
                              IP: {action.ipAddress}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleViewActionsClose}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Friend Details Modal */}
        <Modal show={showFriendModal} onHide={handleCloseModal} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>Friend Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedFriend && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                  <UserAvatarWithFallback
                    imageUrl={getFriendImageUrl(selectedFriend)}
                    name={selectedFriend.name}
                    username={selectedFriend.username}
                    size={60}
                    style={{ marginRight: '15px' }}
                  />
                  <div>
                    <h5 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                      {selectedFriend.name || selectedFriend.username || 'Unknown'}
                    </h5>
                    {selectedFriend.username && selectedFriend.name && (
                      <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                        @{selectedFriend.username}
                      </p>
                    )}
                  </div>
                </div>

                {selectedFriend.tree ? (
                  <div>
                    <div style={{ marginBottom: '15px' }}>
                      <span className={styles.title} style={{ fontSize: '14px' }}>
                        Tree Status:
                      </span>
                      <span
                        style={{
                          marginLeft: '8px',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor: getTreeStatusBgColor(selectedFriend.tree.healthStatus),
                          color: getTreeStatusTextColor(selectedFriend.tree.healthStatus),
                        }}
                      >
                        {getTreeStatusLabel(selectedFriend.tree.healthStatus)}
                      </span>
                    </div>
                    <Row>
                      <Col xs={6} style={{ marginBottom: '12px' }}>
                        <span className={styles.title} style={{ fontSize: '13px' }}>
                          Leaves Grown:
                        </span>
                        <p style={{ margin: '4px 0 0 0', fontSize: '15px', fontWeight: '600' }}>
                          {selectedFriend.tree.leavesGrown || 0}
                        </p>
                      </Col>
                      <Col xs={6} style={{ marginBottom: '12px' }}>
                        <span className={styles.title} style={{ fontSize: '13px' }}>
                          Affirmations Sent:
                        </span>
                        <p style={{ margin: '4px 0 0 0', fontSize: '15px', fontWeight: '600' }}>
                          {selectedFriend.tree.affirmationsSent || 0}
                        </p>
                      </Col>
                      <Col xs={6} style={{ marginBottom: '12px' }}>
                        <span className={styles.title} style={{ fontSize: '13px' }}>
                          Leaves Lost:
                        </span>
                        <p style={{ margin: '4px 0 0 0', fontSize: '15px', fontWeight: '600' }}>
                          {selectedFriend.tree.leavesLost || 0}
                        </p>
                      </Col>
                      <Col xs={6} style={{ marginBottom: '12px' }}>
                        <span className={styles.title} style={{ fontSize: '13px' }}>
                          Last watered:
                        </span>
                        <p style={{ margin: '4px 0 0 0', fontSize: '15px', fontWeight: '600' }}>
                          {selectedFriend.tree.lastWatered
                            ? DateFormatter(
                                selectedFriend.tree.lastWatered,
                                dateFormat || 'toLocaleDateString'
                              )
                            : '-'}
                        </p>
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={6} style={{ marginBottom: '12px' }}>
                        <span className={styles.title} style={{ fontSize: '13px' }}>
                          Cycle:
                        </span>
                        <p style={{ margin: '4px 0 0 0', fontSize: '15px', fontWeight: '600' }}>
                          #{selectedFriend.tree.cycleNumber || 1}
                        </p>
                      </Col>
                    </Row>
                    {selectedFriend.friendsSince && (
                      <div
                        style={{
                          marginTop: '15px',
                          paddingTop: '15px',
                          borderTop: '1px solid #e5e7eb',
                        }}
                      >
                        <span className={styles.title} style={{ fontSize: '13px' }}>
                          Friends Since:
                        </span>
                        <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                          {DateFormatter(
                            selectedFriend.friendsSince,
                            dateFormat || 'toLocaleDateString'
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <p
                      style={{
                        margin: 0,
                        fontSize: '14px',
                        color: '#6b7280',
                        fontStyle: 'italic',
                      }}
                    >
                      No shared tree yet
                    </p>
                    {selectedFriend.friendsSince && (
                      <div style={{ marginTop: '15px' }}>
                        <span className={styles.title} style={{ fontSize: '13px' }}>
                          Friends Since:
                        </span>
                        <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                          {DateFormatter(
                            selectedFriend.friendsSince,
                            dateFormat || 'toLocaleDateString'
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
}

export default ViewUsers;
